<script setup lang="ts">
/*
* Компонент PpuSettings - Раздел «Настройки ППУ» (/improvements/ppusettings)
*
* Назначение:
* - Хранение и редактирование состава экспертов/комиссии в Pinia-сторе (currentCommission и editableCommission)
* - Навигация по вкладкам «Эксперты» и «Виды ППУ»
* - Сохранение состава комиссии через экшен стора saveCurrentCommission(activeTypeId)
*/

import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import ParticipantFormRow from '@/components/adverseEvents/ParticipantFormRow.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import type { ICommissionMember } from '@/modules/improvements/types/IImprovementsStoreState'
import PpuTypeModal from '@/modules/improvements/components/PpuTypeModal.vue'
import type { IImprovementExpert } from '@/modules/improvements/types/IImprovementExpert'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoReload } from '@/modules/onboarding/composables/useOnboardingDemoReload'
import { IMPROVEMENTS_ANCHORS, IMPROVEMENTS_CONTEXTS } from '@/modules/onboarding/scenarios/improvements/anchors'
import { ERouteNames } from '@/router/ERouteNames'
import { useRouter } from 'vue-router'
import type { ISuggestionType } from '@/modules/improvements/types/ISuggestionType'

import { formatDateOnly } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

const { xs } = useDisplay()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()
const improvementsStore = useImprovementsStore()

// Извлечение реактивных данных из стора
const { currentCommission, editableCommission, suggestionTypes, experts } = storeToRefs(improvementsStore)

// 'experts' | 'types'
const mainTab = ref<'experts' | 'types'>('experts')

// Онбординг ППУ заходит сюда веткой из ленты; своё обучение страницы запускается кнопкой рядом с вкладками
const router = useRouter()

useOnboardingContext({
  names: [IMPROVEMENTS_CONTEXTS.settings],
  root: () => document.body,
  actions: {
    backToFeed: async () => { await router.push({ name: ERouteNames.IMPROVEMENTS }) },
    openTypesTab: () => { mainTab.value = 'types' },
    openExpertsTab: () => { mainTab.value = 'experts' },
  },
})
useOnboardingDemoReload('improvement', () => mainTab.value === 'experts'
  ? improvementsStore.fetchExperts()
  : improvementsStore.fetchSuggestionTypes())
const selectedTypeId = ref<string | null>(null)
const isLoadingTypes = ref(false)

const { itemsPerPage } = useTableItemsPerPage('ppuSettingsExperts', 25)
if (![25, 50, 100].includes(itemsPerPage.value))
  itemsPerPage.value = 25
const currentSortString = ref<string | null>(null)
const nextCursor = ref<string | null>(null)

const tableLogic = useTableFilters({
  filters: ref({}),
  itemsPerPage,
  fetchData: async () => {
    await improvementsStore.fetchExperts()
  },
  resetFilters: () => {},
  currentSortString,
  nextCursor,
  sortMap: {},
  loadedItemsCount: computed(() => experts.value.length),
  totalItemsCount: computed(() => experts.value.length),
  filterConfigs: [],
})

const displayedExperts = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return experts.value.slice(start, end)
})

const { itemsPerPage: typesItemsPerPage } = useTableItemsPerPage('ppuSettingsTypes', 25)
const typesSortString = ref<string | null>(null)
const typesNextCursor = ref<string | null>(null)

const typesTableLogic = useTableFilters({
  filters: ref({}),
  itemsPerPage: typesItemsPerPage,
  fetchData: async () => {
    isLoadingTypes.value = true
    try {
      await improvementsStore.fetchSuggestionTypes()
    } finally {
      isLoadingTypes.value = false
    }
  },
  resetFilters: () => {},
  currentSortString: typesSortString,
  nextCursor: typesNextCursor,
  sortMap: {},
  loadedItemsCount: computed(() => suggestionTypes.value.length),
  totalItemsCount: computed(() => improvementsStore.totalSuggestionTypesCount || suggestionTypes.value.length),
  filterConfigs: [],
})

const displayedSuggestionTypes = computed(() => {
  const start = (typesTableLogic.currentPage.value - 1) * typesItemsPerPage.value
  const end = start + typesItemsPerPage.value
  return suggestionTypes.value.slice(start, end)
})

const expertHeaders = [
  { title: '№', key: 'number', width: '60px', sortable: false },
  { title: 'Эксперт', key: 'employee', sortable: false },
  { title: 'Отделение', key: 'department', sortable: false },
  { title: 'Рассмотрено ППУ', key: 'ppu_member', sortable: false },
  { title: 'Дата добавления', key: 'created_at', sortable: false },
  { title: 'Действия', key: 'actions', width: '80px', sortable: false, align: 'end' },
] as const

const showDeleteExpertDialog = ref(false)
const expertIdToDelete = ref<number | null>(null)
const expertNameToDelete = ref('')

const requestDeleteExpert = (item: IImprovementExpert) => {
  expertIdToDelete.value = item.id
  expertNameToDelete.value = item.employee?.full_name || getFullName(item.employee)
  showDeleteExpertDialog.value = true
}

const confirmDeleteExpert = async (confirmed: boolean) => {
  showDeleteExpertDialog.value = false
  if (!confirmed) {
    expertIdToDelete.value = null
    expertNameToDelete.value = ''
    return
  }

  const id = expertIdToDelete.value
  expertIdToDelete.value = null
  expertNameToDelete.value = ''
  if (id == null) return
  await improvementsStore.deleteExpert(id)
}

const isPpuTypeModalOpen = ref(false)
const selectedPpuType = ref<ISuggestionType | null>(null)

const openCreatePpuTypeModal = () => {
  selectedPpuType.value = null
  isPpuTypeModalOpen.value = true
}

const openEditPpuTypeModal = (typeItem: ISuggestionType) => {
  selectedPpuType.value = typeItem
  isPpuTypeModalOpen.value = true
}

function onRowClickPpuType(_: unknown, row: { item: ISuggestionType }) {
  openEditPpuTypeModal(row.item)
}

const showDeletePpuTypeDialog = ref(false)
const ppuTypeIdToDelete = ref<string | null>(null)
const ppuTypeNameToDelete = ref('')

const requestDeletePpuType = (item: ISuggestionType) => {
  ppuTypeIdToDelete.value = item.id
  ppuTypeNameToDelete.value = item.name || ''
  showDeletePpuTypeDialog.value = true
}

const confirmDeletePpuType = async (confirmed: boolean) => {
  showDeletePpuTypeDialog.value = false
  if (!confirmed) {
    ppuTypeIdToDelete.value = null
    ppuTypeNameToDelete.value = ''
    return
  }

  const id = ppuTypeIdToDelete.value
  ppuTypeIdToDelete.value = null
  ppuTypeNameToDelete.value = ''
  if (id == null) return
  await improvementsStore.deleteSuggestionType(id)
}

const toggleTypeActive = async (item: ISuggestionType, isActive: boolean) => {
  if (item.is_active === isActive) return
  await improvementsStore.toggleSuggestionTypeActive(item.id, isActive)
}

const typeHeaders = [
  { title: '№', key: 'number', width: '60px', sortable: false },
  { title: 'Наименование', key: 'name', sortable: false },
  { title: 'Председатель комиссии', key: 'chair', sortable: false },
  { title: 'Активность', key: 'is_active', sortable: false },
  { title: 'Действия', key: 'actions', width: '100px', sortable: false, align: 'end' },
] as const

// Текущий выбранный вид ППУ
const activeType = computed(() => {
  if (!selectedTypeId.value && suggestionTypes.value.length) {
    return suggestionTypes.value[0]
  }
  return suggestionTypes.value.find(t => t.id === selectedTypeId.value) || null
})

// При изменении выбранного вида ППУ подгружаем состав его комиссии
watch(selectedTypeId, (newTypeId) => {
  if (newTypeId) {
    improvementsStore.setCurrentCommissionForType(newTypeId)
  }
}, { immediate: true })

watch(suggestionTypes, (newTypes) => {
  if (newTypes.length && !selectedTypeId.value) {
    selectedTypeId.value = newTypes[0].id
    improvementsStore.setCurrentCommissionForType(newTypes[0].id)
  }
}, { immediate: true })

const getFullName = (person: any) => {
  if (!person) return ''
  return [person.last_name, person.first_name, person.middle_name].filter(Boolean).join(' ')
}

const fallbackAvatars = new Map<string, string>()

function getExpertAvatar(employee?: { id?: string; avatar?: string | null } | null): string {
  if (employee?.avatar) return employee.avatar
  const id = String(employee?.id || 'default')
  if (!fallbackAvatars.has(id))
    fallbackAvatars.set(id, getRandomAvatar())
  return fallbackAvatars.get(id)!
}

// Состояние модального окна добавления членов комиссии
const isAddMemberModalOpen = ref(false)

// Состояние диалога подтверждения закрытия при несохраненных изменениях
const showUnsavedChangesDialog = ref(false)

// Состояние модального окна выбора сотрудников
const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const selectedEmployeeId = ref<string>('')

const draftSelectedEmployees = computed(() => {
  return currentCommission.value.map(m => ({
    id: String(m.id),
    full_name: m.name,
  } as IEmployeeTreeItem))
})

watch(mainTab, async (newTab) => {
  if (newTab === 'experts') {
    await improvementsStore.fetchExperts()
  } else if (newTab === 'types') {
    isLoadingTypes.value = true
    try {
      await improvementsStore.fetchSuggestionTypes()
    } finally {
      isLoadingTypes.value = false
    }
  }
}, { immediate: true })

function getRoleChipProps(role: string) {
  if (role === 'Председатель') {
    return {
      color: 'info',
      variant: 'tonal' as const,
      class: 'status-badge font-weight-medium px-3',
    }
  }
  return {
    color: 'secondary',
    variant: 'tonal' as const,
    class: 'status-badge font-weight-regular px-3 text-medium-emphasis',
  }
}

const openAddMemberModal = async () => {
  currentCommission.value = []
  await nextTick()
  openEmployeeSelection()
}

/**
 * Проверка наличия изменений в форме комиссии (паттерн аудитов)
 */
const hasFormChanges = (): boolean => {
  return currentCommission.value.length > 0
}

/**
 * Попытка закрыть модальное окно с проверкой несохраненных изменений
 */
const handleCloseModal = () => {
  isAddMemberModalOpen.value = false
}

const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal)

/**
 * Обработчик подтверждения закрытия при несохраненных изменениях
 */
const handleCloseConfirm = (value: boolean) => {
  showUnsavedChangesDialog.value = false
  isAddMemberModalOpen.value = false
}

const openEmployeeSelection = () => {
  employeeModalRef.value?.open()
}

const onMultipleEmployeesSelected = (employees: IEmployeeTreeItem[]) => {
  const existingMap = new Map(currentCommission.value.map(m => [String(m.id), m]))

  currentCommission.value = employees.map(emp => {
    const existing = existingMap.get(String(emp.id))
    const position = emp.position_name || ''
    return {
      id: emp.id,
      name: emp.full_name,
      position: existing ? existing.position : position,
      department: existing ? existing.department : '',
      role: 'Эксперт',
      avatarColor: existing ? existing.avatarColor : 'primary',
    }
  })
  isAddMemberModalOpen.value = true
}

const deleteDraftMember = (id: string) => {
  currentCommission.value = currentCommission.value.filter(m => String(m.id) !== String(id))
}

const saveCommissionMembers = async () => {
  if (!currentCommission.value.length) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Выберите хотя бы одного эксперта',
      time: 3000,
    })
    return
  }

  const employeeIds = currentCommission.value.map(m => String(m.id))
  const isSaved = await improvementsStore.addExperts(employeeIds)
  if (isSaved) {
    isAddMemberModalOpen.value = false
    await improvementsStore.fetchExperts()
  }
}
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <!-- Главные вкладки страницы и кнопка обучения -->
    <div class="d-flex align-center justify-space-between gap-2">
      <VTabs
        v-model="mainTab"
        color="primary"
        style="width: max-content; max-width: 100%; flex-shrink: 0;"
      >
        <div class="d-flex gap-2">
          <VTab value="experts" :data-onboarding="IMPROVEMENTS_ANCHORS.settings.expertsTab">
            Эксперты
          </VTab>
          <VTab value="types" :data-onboarding="IMPROVEMENTS_ANCHORS.settings.typesTab">
            Виды ППУ
          </VTab>
        </div>
      </VTabs>
      <OnboardingLaunchButton scenario="improvements.settings" />
    </div>

    <!-- Вкладка 0: Эксперты -->
    <template v-if="mainTab === 'experts'">
      <VCard class="border overflow-hidden" elevation="0" :data-onboarding="IMPROVEMENTS_ANCHORS.settings.expertsPanel">
        <div
          class="d-flex align-center pa-4"
          :class="{ 'flex-column': xs }"
        >
          <VBtn
            :block="xs"
            variant="elevated"
            color="primary"
            prepend-icon="ri-add-line"
            :data-onboarding="IMPROVEMENTS_ANCHORS.settings.addExpert"
            @click="openAddMemberModal"
          >
            Добавить эксперта
          </VBtn>
        </div>

        <VDataTableServer
          :headers="expertHeaders"
          :items="displayedExperts"
          :items-length="experts.length"
          :items-per-page="itemsPerPage"
          :loading="improvementsStore.isLoading"
          class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer"
          @update:options="tableLogic.handlePageChange"
        >
          <template #no-data>
            <div class="text-center text-italic py-4 text-medium-emphasis">
              Эксперты не добавлены
            </div>
          </template>

          <template #[`item.number`]="{ index }">
            <span>{{ (tableLogic.currentPage.value - 1) * itemsPerPage + index + 1 }}</span>
          </template>

          <template #[`item.employee`]="{ item }">
            <div class="d-flex align-center gap-3 py-2">
              <VAvatar size="38">
                <VImg :src="getExpertAvatar(item.employee)" />
              </VAvatar>
              <div class="d-flex flex-column">
                <span class="font-weight-medium">{{ item.employee?.full_name || getFullName(item.employee) }}</span>
                <span class="text-caption text-medium-emphasis">
                  {{ item.employee?.position_name || item.employee?.position?.name || '' }}
                </span>
              </div>
            </div>
          </template>

          <template #[`item.department`]="{ item }">
            <span>{{ item.employee?.department_name || item.employee?.department?.short_name || item.employee?.department?.name || '' }}</span>
          </template>

          <template #[`item.ppu_member`]="{ item }">
            <span>{{ item.ppu_member ?? '' }}</span>
          </template>

          <template #[`item.created_at`]="{ item }">
            <span>{{ formatDateOnly(item.created_at) }}</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-end">
              <VIconBtn
                :rounded="false"
                :size="32"
                icon="ri-delete-bin-3-line"
                variant="text"
                color="secondary"
                @click.stop="requestDeleteExpert(item)"
              />
            </div>
          </template>

          <template #bottom>
            <div
              class="custom-data-table-footer"
              :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
            >
              <VDataTableFooter
                show-current-page
                :items-per-page-options="[25, 50, 100]"
                v-bind="$attrs"
                @click:prev="$emit('click:prev', $event)"
                @click:next="$emit('click:next', $event)"
                @click:first="$emit('click:first', $event)"
                @click:last="$emit('click:last', $event)"
              />
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </template>

    <!-- Вкладка 1: Виды ППУ -->
    <template v-else-if="mainTab === 'types'">
      <VCard class="border overflow-hidden" elevation="0">
        <div
          class="d-flex align-center pa-4"
          :class="{ 'flex-column': xs }"
        >
          <VBtn
            :block="xs"
            variant="elevated"
            color="primary"
            prepend-icon="ri-add-line"
            :data-onboarding="IMPROVEMENTS_ANCHORS.settings.createType"
            @click="openCreatePpuTypeModal"
          >
            Создать вид ППУ
          </VBtn>
        </div>

        <VDataTableServer
          :headers="typeHeaders"
          :items="displayedSuggestionTypes"
          :items-length="improvementsStore.totalSuggestionTypesCount || suggestionTypes.length"
          :items-per-page="typesItemsPerPage"
          :loading="isLoadingTypes"
          class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer"
          @update:options="typesTableLogic.handlePageChange"
          @click:row="onRowClickPpuType"
        >
          <template #no-data>
            <div class="text-center text-italic py-4 text-medium-emphasis">
              Виды ППУ не найдены
            </div>
          </template>

          <template #[`item.number`]="{ index }">
            <span>{{ (typesTableLogic.currentPage.value - 1) * typesItemsPerPage + index + 1 }}</span>
          </template>

          <template #[`item.name`]="{ item }">
            <span class="font-weight-medium">{{ item.name }}</span>
          </template>

          <template #[`item.chair`]="{ item }">
            <ShortFio :person="item.chair" />
          </template>


          <template #[`header.is_active`]="{ column }">
            <div class="d-flex align-center ga-1">
              <span>{{ column.title }}</span>
              <VTooltip location="top">
                <template #activator="{ props: tipProps }">
                  <span
                    v-bind="tipProps"
                    class="d-inline-flex activity-info-icon"
                  >
                    <VIcon
                      icon="ri-information-line"
                      :size="16"
                      color="secondary"
                    />
                  </span>
                </template>
                При включенной активности вид ППУ доступен для выбора при создании ППУ
              </VTooltip>
            </div>
          </template>

          <template #[`item.is_active`]="{ item }">
            <div @click.stop>
              <VSwitch
                :model-value="item.is_active"
                color="primary"
                hide-details
                density="compact"
                inset
                @update:model-value="toggleTypeActive(item, Boolean($event))"
              />
            </div>
          </template>

          <template #[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-end ga-3">
              <VIconBtn
                v-if="!item.is_daomed_main && !item.is_preset"
                :rounded="false"
                :size="32"
                icon="ri-delete-bin-3-line"
                variant="text"
                color="secondary"
                @click.stop="requestDeletePpuType(item)"
              />
              <VIconBtn
                :rounded="false"
                :size="32"
                icon="ri-edit-line"
                variant="text"
                color="secondary"
                @click.stop="openEditPpuTypeModal(item)"
              />
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </template>

    <!-- Модальное окно добавления членов комиссии -->
    <VDialog
      v-model="isAddMemberModalOpen"
      max-width="1000"
      persistent
      content-class="entity-form-menu"
      @click:outside="onDialogDismissAttempt"
      @keydown.esc.stop="onDialogDismissAttempt"
    >
      <VCard>
        <VCardTitle class="d-flex pa-6" :class="xs ? 'px-6 py-4' : 'pa-6'">
          <div class="form_card d-flex justify-space-between w-100">
            <div class="form_card__title">
              <span class="text-h4">Добавление экспертов ППУ</span>
            </div>
          </div>
          <CustomCloseButton :close-action="handleCloseModal" />
        </VCardTitle>

        <VCardText>
          <div class="d-flex flex-column gap-4 px-0">
            <!-- Кнопка "Выбрать экспертов" со сплит-стрелкой -->
            <div class="d-flex align-center">
              <VBtn
                :class="{ 'flex-grow-1': xs }"
                variant="outlined"
                prepend-icon="ri-user-add-line"
                class="rounded-te-0 rounded-be-0"
                @click="openEmployeeSelection"
              >
                Выбрать экспертов
              </VBtn>
              <VIconBtn
                color="primary"
                class="rounded-ts-0 rounded-ds-0 rounded-te rounded-be"
                icon="ri-arrow-down-s-line"
                variant="outlined"
                :rounded="false"
                size="38"
                @click="openEmployeeSelection"
              />
            </div>

            <!-- Список выбранных сотрудников в модалке -->
            <div v-if="currentCommission.length" class="d-flex flex-column gap-3 w-100">
              <ParticipantFormRow
                v-for="(member, index) in currentCommission"
                :id="String(member.id)"
                :key="member.id"
                :index="index + 1"
                :full_name="member.name"
                :employee_position_name="member.position"
                :is-ppu-participant="true"
                :show_delete_button="true"
                @delete="deleteDraftMember"
              />
            </div>

            <div class="d-flex align-center mt-2" :class="xs ? 'flex-column' : 'justify-end'">
              <VBtn
                :block="xs"
                color="primary"
                @click="saveCommissionMembers"
              >
                Сохранить
              </VBtn>
            </div>
          </div>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Диалог подтверждения закрытия при несохраненных изменениях -->
    <ConfirmDialog
      v-model:is-dialog-visible="showUnsavedChangesDialog"
      confirmation-question="Закрыть без сохранения?"
      confirmation-text="Процесс изменения состава комиссии будет прерван, данные не сохранятся"
      confirm-btn="Закрыть"
      reject-btn="Отменить"
      @confirm="handleCloseConfirm"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="showDeleteExpertDialog"
      confirmation-question="Удалить эксперта?"
      :confirmation-text="expertNameToDelete
        ? `Вы действительно хотите удалить эксперта «${expertNameToDelete}»? Это действие нельзя отменить.`
        : 'Это действие нельзя отменить.'"
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="confirmDeleteExpert"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="showDeletePpuTypeDialog"
      confirmation-question="Удалить вид ППУ?"
      :confirmation-text="ppuTypeNameToDelete
        ? `Вы действительно хотите удалить вид ППУ «${ppuTypeNameToDelete}»? Это действие нельзя отменить.`
        : 'Это действие нельзя отменить.'"
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="confirmDeletePpuType"
    />

    <!-- Модальное окно выбора сотрудников из дерева -->
    <EmployeeSelectionModal
      ref="employeeModalRef"
      v-model="selectedEmployeeId"
      :employees="apiStore.employees"
      multiple
      :selected-employees="draftSelectedEmployees"
      @select-multiple="onMultipleEmployeesSelected"
    />

    <!-- Модальное окно создания/редактирования вида ППУ -->
    <PpuTypeModal
      v-model="isPpuTypeModalOpen"
      :suggestion-type="selectedPpuType"
      @saved="() => improvementsStore.fetchSuggestionTypes()"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

/* Шапка таблицы 15px; VIcon по умолчанию 1.5em (~22px). Макет — 16×16. */
.activity-info-icon :deep(i) {
  font-size: 16px !important;
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }

  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}
</style>
